// ===============================================================================
// InstallPWA.js - PWA Install Prompt Component for SplitMon
// ===============================================================================
// Location: /var/www/wow/frontend/src/InstallPWA.js
//
// FEATURES:
// - Captures browser install prompt
// - Shows custom install banner
// - Handles iOS "Add to Home Screen" instructions
// - Tracks installation status
// - Shows app update notifications
// ===============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import './InstallPWA.css';

function InstallPWA() {
  // ===========================================================================
  // State
  // ===========================================================================
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [swRegistration, setSwRegistration] = useState(null);

  // ===========================================================================
  // Detect iOS
  // ===========================================================================
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  };

  const isInStandaloneMode = () => {
    return window.matchMedia('(display-mode: standalone)').matches || 
           window.navigator.standalone === true;
  };

  // ===========================================================================
  // Check if should show install prompt
  // ===========================================================================
  const shouldShowInstall = useCallback(() => {
    // Don't show if already installed
    if (isInStandaloneMode()) {
      return false;
    }

    // Check if user dismissed recently (24 hours)
    const dismissedAt = localStorage.getItem('pwa-install-dismissed');
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const hoursSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60);
      if (hoursSinceDismissed < 24) {
        return false;
      }
    }

    return true;
  }, []);

  // ===========================================================================
  // Effects
  // ===========================================================================
  useEffect(() => {
    // Check if already installed
    if (isInStandaloneMode()) {
      setIsInstalled(true);
      return;
    }

    // Listen for install prompt (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      
      if (shouldShowInstall()) {
        // Delay showing banner slightly for better UX
        setTimeout(() => setShowBanner(true), 2000);
      }
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowBanner(false);
      setInstallPrompt(null);
      console.log('🎉 PWA installed successfully!');
    };

    // Listen for custom events from index.js
    const handleInstallAvailable = (e) => {
      setInstallPrompt(e.detail.prompt);
      if (shouldShowInstall()) {
        setTimeout(() => setShowBanner(true), 2000);
      }
    };

    const handlePwaInstalled = () => {
      setIsInstalled(true);
      setShowBanner(false);
    };

    // Listen for service worker updates
    const handleSwUpdate = (e) => {
      setSwRegistration(e.detail.registration);
      setUpdateAvailable(true);
    };

    // Listen for network status changes
    const handleNetworkChange = (e) => {
      setIsOnline(e.detail.online);
    };

    // Add event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('pwaInstallAvailable', handleInstallAvailable);
    window.addEventListener('pwaInstalled', handlePwaInstalled);
    window.addEventListener('swUpdateAvailable', handleSwUpdate);
    window.addEventListener('networkStatusChange', handleNetworkChange);
    window.addEventListener('online', () => setIsOnline(true));
    window.addEventListener('offline', () => setIsOnline(false));

    // Show iOS instructions if on iOS and not installed
    if (isIOS() && !isInStandaloneMode() && shouldShowInstall()) {
      setTimeout(() => setShowIOSInstructions(true), 3000);
    }

    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('pwaInstallAvailable', handleInstallAvailable);
      window.removeEventListener('pwaInstalled', handlePwaInstalled);
      window.removeEventListener('swUpdateAvailable', handleSwUpdate);
      window.removeEventListener('networkStatusChange', handleNetworkChange);
    };
  }, [shouldShowInstall]);

  // ===========================================================================
  // Handlers
  // ===========================================================================
  const handleInstall = async () => {
    if (!installPrompt) return;

    // Show the browser's install prompt
    installPrompt.prompt();
    
    // Wait for user response
    const { outcome } = await installPrompt.userChoice;
    
    console.log(`Install prompt ${outcome}`);
    
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    
    // Clear the prompt
    setInstallPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSInstructions(false);
    // Remember dismissal for 24 hours
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  const handleUpdate = () => {
    if (swRegistration && swRegistration.waiting) {
      // Tell the waiting service worker to activate
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    setUpdateAvailable(false);
  };

  // ===========================================================================
  // Don't render if installed or nothing to show
  // ===========================================================================
  if (isInstalled) {
    return null;
  }

  // ===========================================================================
  // Render
  // ===========================================================================
  return (
    <>
      {/* Offline Indicator */}
      {!isOnline && (
        <div className="pwa-offline-indicator">
          <span className="offline-icon">📴</span>
          <span>You're offline</span>
        </div>
      )}

      {/* Update Available Banner */}
      {updateAvailable && (
        <div className="pwa-update-banner">
          <div className="update-content">
            <span className="update-icon">🆕</span>
            <div className="update-text">
              <strong>Update Available</strong>
              <span>A new version of SplitMon is ready</span>
            </div>
          </div>
          <div className="update-actions">
            <button className="update-dismiss" onClick={() => setUpdateAvailable(false)}>
              Later
            </button>
            <button className="update-button" onClick={handleUpdate}>
              Update Now
            </button>
          </div>
        </div>
      )}

      {/* Install Banner (Android/Chrome) */}
      {showBanner && installPrompt && (
        <div className="pwa-install-banner">
          <div className="install-content">
            <div className="install-icon">
              <span>💰</span>
            </div>
            <div className="install-text">
              <strong>Install SplitMon</strong>
              <span>Add to home screen for quick access</span>
            </div>
          </div>
          <div className="install-actions">
            <button className="install-dismiss" onClick={handleDismiss}>
              Not Now
            </button>
            <button className="install-button" onClick={handleInstall}>
              Install
            </button>
          </div>
        </div>
      )}

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="pwa-ios-modal-overlay" onClick={handleDismiss}>
          <div className="pwa-ios-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ios-modal-close" onClick={handleDismiss}>×</button>
            
            <div className="ios-modal-header">
              <span className="ios-modal-icon">💰</span>
              <h2>Install SplitMon</h2>
              <p>Add to your home screen for the best experience</p>
            </div>
            
            <div className="ios-instructions">
              <div className="ios-step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <span>Tap the <strong>Share</strong> button</span>
                  <div className="share-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/>
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="ios-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <span>Scroll and tap <strong>"Add to Home Screen"</strong></span>
                  <div className="add-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <path d="M12 8v8M8 12h8"/>
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="ios-step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <span>Tap <strong>"Add"</strong> to confirm</span>
                </div>
              </div>
            </div>
            
            <button className="ios-got-it" onClick={handleDismiss}>
              Got it!
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default InstallPWA;
